import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { Client, CognitoUserPool, Section, EmailName } from '@mira-avant/mtlibs';
import { NavigationService } from '../../../../services/navigation.service';
import { CognitoService } from '../../../../services/cognito.service';
import { ClientService } from '../../../../services/client.service';
import { UserService } from '../../../../services/user.service';
import { LanguageService } from '../../../../services/language.service';
import { CognitoGroupSelectorComponent } from '../../../ui/admin/cognito-group-selector/cognito-group-selector.component';
import { AngularModule } from '../../../../modules/angular.module';
import { ZorroModule } from '../../../../modules/zorro.module';
import { AdminService } from '../../../../services/admin.service';
import { ClientSelectorComponent } from '../../../ui/admin/client-selector/client-selector.component';
import { FieldLabelComponent } from '../../../ui/forms/field-label/field-label.component';
import { SectionsSelectorComponent } from '../../../ui/admin/sections-selector/sections-selector.component';

@Component({
	selector: 'app-generate-emails-users-form',
	standalone: true,
	imports: [
		AngularModule,
		ZorroModule,
		CognitoGroupSelectorComponent,
		ClientSelectorComponent,
		FieldLabelComponent,
		SectionsSelectorComponent,
	],
	templateUrl: './generate-emails-users-form.component.html',
	styleUrl: './generate-emails-users-form.component.scss',
})
export class GenerateEmailsUsersFormComponent implements OnInit {
	@Input('emails') public emails: EmailName[] = [];
	@Output('onSubmit') public onSubmitEmitter: EventEmitter<void> = new EventEmitter<void>();
	private sub: Subscription | undefined;
	public loading: boolean = false;
	public groups: string[] = [];
	public selectedClient: Client | undefined;
	public selectedSections: Section[] = [];
	public selectedSectionUuids: string[] = [];
	public selectedGroups: string[] = [];
	public activeStartDate: Date | undefined;
	public activeEndDate: Date | undefined;
	public active: boolean = true;

	constructor(
		public nav: NavigationService,
		public cognitoService: CognitoService,
		public clientService: ClientService,
		public userService: UserService,
		public languageService: LanguageService,
		public adminService: AdminService
	) {}

	ngOnInit() {
		this.sub = this.adminService.selectedUserPool$.subscribe((pool: CognitoUserPool | undefined) => {
			if (!pool) {
				return;
			}

			this.cognitoService.listUserGroups(pool.id).then((groups: string[]) => {
				this.groups = groups;
			});
		});
	}

	public onClientSelect(client: Client): void {
		this.selectedClient = client;
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.reset$();
	}

	private async reset$(): Promise<void> {
		this.selectedClient = undefined;
		this.selectedSections = [];
		this.selectedGroups = [];
	}

	public onSelectClient(client: Client) {
		this.selectedClient = client;
	}

	public onSectionsChanged(sections: Section[]): void {
		this.selectedSections = sections;
		this.selectedSectionUuids = sections.map((section: Section) => section.uuid);
	}

	public onGroupsChanged(groups: string[]): void {
		this.selectedGroups = groups;
	}

	public async onSubmit$() {
		try {
			this.loading = true;

			const userPoolId: string | undefined = this.adminService.selectedUserPool$.value?.id;

			if (!userPoolId) {
				throw new Error('No user pool selected');
			}

			if (!this.selectedClient) {
				throw new Error('No client selected');
			}

			await this.cognitoService.bulkOnboardEmailsUsers(
				this.emails,
				this.selectedClient,
				this.selectedSections,
				this.selectedGroups,
				this.activeStartDate,
				this.activeEndDate,
				this.active,
				userPoolId
			);

			// Notify parent component
			this.onSubmitEmitter.emit();
		} catch (error: any) {
			alert('Error creating and updating users: ' + error.message);
		} finally {
			this.loading = false;
		}
	}

	ngOnDestroy() {
		if (this.sub) {
			this.sub.unsubscribe();
		}
	}
}
